import { useEffect, useMemo, useState } from 'react';
import { responseErrorsResolver, ResponseError } from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  onPageChange,
  onSectionChange,
  onFilterChange,
} from './store/slices/roles';
import { EditRoleRequest, PermissionEnum } from './types';
import { handleConfirmPopup } from './store/slices/confirm';
import ColumnWithConfirmHOC from './column-with-confirm';
import ColumnWithToggle from '../../components/common/column/column-with-toggle';
import { useEditRoleMutation } from './store/services/roles';
import getPreparePermissions from '../user-list/helpers/get-permissions';
import permissionsTree from './variables';
import sectionActions from './all-sections';
import {
  onChangeHelperArray,
  onChangeHelperSingle,
} from '../user-list/helpers/on-change-helper';

import cn from './styles.module.css';

const PermissionColumns = ({
  isLoading,
}: {
  isLoading: boolean;
}): React.JSX.Element => {
  const dispatch = useAppDispatch();

  const {
    userRoles: {
      roles: {
        selectedRole,
        roleId,
        sectionId,
        pageId,
        search,
        allActionsSectionFiltered,
      },
    },
    administrationDictionaries: {
      activePermissions,
      allPermissions,
      cardsEnabled,
    },
  } = useAppSelector((state) => state);

  const [values, setValues] = useState(selectedRole?.permissions || []);
  const [globalError, setGlobalError] = useState('');

  const permissionEnumKeys = Object.keys(PermissionEnum).map((item) => item);
  const unassignedPermissions = allPermissions?.filter((item) =>
    permissionEnumKeys.every((permission) => item !== permission),
  );

  let nextPermissionsTree = permissionsTree;

  if (!cardsEnabled) {
    nextPermissionsTree = permissionsTree.filter(
      (item) => item.key !== PermissionEnum.SECTION_CARDS,
    );
  }

  if (unassignedPermissions?.length) {
    const unassignedPermissionsPages = unassignedPermissions.filter(
      (item) => !item.includes('_ACTION'),
    );

    nextPermissionsTree = [
      ...nextPermissionsTree,
      {
        key: 'SECTION_UNASSIGNED' as PermissionEnum,
        pages: [
          {
            key: 'UNASSIGNED_LIST' as PermissionEnum,
            actions: unassignedPermissions
              .filter((item) => item.includes('_ACTION'))
              .map((item) => ({
                key: item,
              })),
          },
          ...unassignedPermissionsPages.map((item) => ({
            key: item,
          })),
        ],
      },
    ];
  }

  const columnsData = getPreparePermissions(nextPermissionsTree);

  const [edit, { isLoading: editIsLoading, isSuccess }] = useEditRoleMutation();

  const sections = roleId ? columnsData : undefined;
  const pages = roleId && sectionId ? columnsData[sectionId]?.pages : undefined;
  const actions =
    roleId && sectionId && pageId
      ? columnsData[sectionId]?.pages?.[pageId]?.actions
      : undefined;

  const activeSections = useMemo(
    () => values.filter((value) => value.includes('SECTION')),
    [values],
  );
  const activePages = useMemo(
    () =>
      values.filter(
        (value) => !value.includes('SECTION') && !value.includes('_ACTION'),
      ),
    [values],
  );
  const activeActions = useMemo(
    () => values.filter((value) => value.includes('_ACTION')),
    [values],
  );

  const onSearchPermissions = (value: string) => {
    dispatch(onFilterChange(value));
  };

  const onChange = (
    key: PermissionEnum | PermissionEnum[],
    value: boolean,
    columnsDataItem: any,
  ) => {
    dispatch(handleConfirmPopup(true));

    if (Array.isArray(key)) {
      onChangeHelperArray(
        key,
        value,
        columnsDataItem,
        columnsData,
        setValues,
        activePages,
        pageId,
      );
    } else {
      onChangeHelperSingle(
        key,
        value,
        columnsDataItem,
        columnsData,
        setValues,
        activePages,
        activeActions,
      );
    }
  };

  const onSelectRow = (
    key: PermissionEnum | 'all',
    name: 'sectionId' | 'pageId',
  ) => {
    if (name === 'sectionId') {
      dispatch(onSectionChange(key as PermissionEnum));
    } else if (name === 'pageId') {
      dispatch(onPageChange(key as PermissionEnum));
    }
  };

  useEffect(() => {
    setValues(selectedRole?.permissions || []);
  }, [selectedRole?.permissions]);

  const onSubmit = async () => {
    setGlobalError('');

    const body = {
      permissions: values.filter(
        (item) =>
          item !== ('SECTION_UNASSIGNED' as PermissionEnum) &&
          item !== ('UNASSIGNED_LIST' as PermissionEnum) &&
          item !== ('CUSTOMERS_SESSIONS_DELETE' as PermissionEnum) &&
          item !== ('EMPLOYEES_SESSIONS_DELETE' as PermissionEnum) &&
          item !== PermissionEnum.OTHER_LIST,
      ),
      name: selectedRole?.name,
      active: selectedRole?.active,
    };

    const response = (await edit({
      body: body as EditRoleRequest,
      id: selectedRole?.id as string,
    })) as { error?: ResponseError };

    if (response.error) {
      responseErrorsResolver({
        errors: response.error,
        setError: () => {},
        setGlobalError,
      });
    }
  };

  useEffect(() => {
    if (isSuccess) {
      dispatch(handleConfirmPopup(false));
    }
  }, [isSuccess]);

  const onCancel = () => {
    dispatch(handleConfirmPopup(false));
    setValues(selectedRole?.permissions || []);
  };

  const isEditable = activePermissions?.includes(
    PermissionEnum.USERS_ROLES_EDIT_ACTION,
  );

  const allActionsSection =
    roleId && sectionId ? sectionActions[sectionId] : undefined;

  return (
    <ColumnWithConfirmHOC
      onSubmit={onSubmit}
      onCancel={onCancel}
      editIsLoading={editIsLoading}
      globalError={globalError}
    >
      <div className={cn.columnWrapper}>
        <ColumnWithToggle
          activePermissions={activeSections}
          columnData={sections}
          isLoading={isLoading}
          onChange={onChange}
          isEditable={isEditable}
          onSelectRow={(key: PermissionEnum | 'all') =>
            onSelectRow(key, 'sectionId')
          }
          selectedId={sectionId}
          title="Sections"
          description={`for ${selectedRole ? selectedRole.name : ''}`}
          emptyTitle="—"
          hasAllToggleButton={false}
        />
      </div>

      <div className={cn.columnWrapper}>
        <ColumnWithToggle
          activePermissions={activePages}
          columnData={pages}
          isLoading={isLoading}
          onChange={onChange}
          isEditable={isEditable}
          onSelectRow={(key: PermissionEnum | 'all') =>
            onSelectRow(key, 'pageId')
          }
          selectedId={pageId}
          allActionsSectionButton={Boolean(allActionsSection)}
          title="Pages"
          emptyTitle="—"
        />
      </div>

      <div className={cn.columnWrapper}>
        <ColumnWithToggle
          activePermissions={activeActions}
          columnData={pageId !== 'all' ? actions : allActionsSectionFiltered}
          isLoading={isLoading}
          onChange={onChange}
          selectedId={pageId}
          isEditable={isEditable}
          title="Action permissions"
          emptyTitle="View only"
          onSearch={onSearchPermissions}
          search={search}
        />
      </div>
    </ColumnWithConfirmHOC>
  );
};

export default PermissionColumns;
